import { useEffect, useRef, useState } from 'react';
import type { PluginDefinition, PluginManifestEntry } from './types';
import { bootstrapPlugins, syncServerEnablement } from './pluginLoader';
import { pluginRegistry } from '@/plugins/pluginRegistry';
import { usePluginStore } from '@/stores/usePluginStore';
import { useOrgPluginStore } from '@/stores/useOrgPluginStore';
import { loadManifestSources } from './manifestApi';
import { useApiToken } from '@/auth/hooks';
import { knowledgeCapturePlugin } from '@/plugins/external/knowledgeCapturePlugin';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useLocation } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { usePlatformSettingsStore } from '@/stores/usePlatformSettingsStore';

interface PluginBootstrapperProps {
  plugins: PluginDefinition[];
  manifest?: PluginManifestEntry[];
  hostVersion?: string;
  hostCapabilities?: string[];
}

export function PluginBootstrapper({
  plugins,
  manifest,
  hostVersion,
  hostCapabilities,
}: PluginBootstrapperProps) {
  const [resolvedManifest, setResolvedManifest] = useState<PluginManifestEntry[] | undefined>(manifest);
  const { getAccessToken } = useApiToken();
  const { inProgress } = useMsal();
  const location = useLocation();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspace?.id ?? '');
  const isProtectedAppRoute = location.pathname === '/app' || location.pathname.startsWith('/app/');
  const fetchVoiceSettings = useVoiceStore((s) => s.fetchVoiceSettings);
  const platformSettingsLoaded = usePlatformSettingsStore((s) => s.loaded);
  const fetchPlatformSettings = usePlatformSettingsStore((s) => s.fetchSettings);
  const {
    enabledIds,
    seenPluginIds,
    hydrated,
    booted,
    setBooted,
    setEnabledIds,
    setSeenPluginIds,
    setErrors,
    fetchWorkspaceSettings,
    workspaceSettings,
  } = usePluginStore();
  const { fetchOrgPluginSettings, orgPluginSettings, loaded: orgSettingsLoaded, getOrgDisabledIds } = useOrgPluginStore();
  const subscribedRef = useRef(false);

  function mergeNewDefaultEnablement(options: {
    enabledIds: string[];
    seenPluginIds: string[];
    plugins: PluginDefinition[];
    manifest?: PluginManifestEntry[];
  }) {
    const enabled = new Set(options.enabledIds);
    const seen = new Set(options.seenPluginIds);

    const manifestDefaults = new Set(
      (options.manifest ?? [])
        .filter((entry) => entry.enabledByDefault)
        .map((entry) => entry.id)
    );

    // Always bring newly-introduced enabledByDefault plugins online once.
    manifestDefaults.forEach((id) => {
      if (!seen.has(id)) {
        enabled.add(id);
      }
    });

    // Locked internal plugins must always be enabled.
    options.plugins.forEach((plugin) => {
      if (plugin.internal && plugin.locked) {
        enabled.add(plugin.id);
      }
    });

    // Enable new internal plugins whose requirements are enabled.
    // We only do this for plugins that the user has never seen before, so we
    // don't override an explicit user disable.
    let changed = true;
    while (changed) {
      changed = false;
      options.plugins.forEach((plugin) => {
        if (!plugin.internal) {
          return;
        }
        if (seen.has(plugin.id) || enabled.has(plugin.id)) {
          return;
        }
        const requires = plugin.requires ?? [];
        if (requires.length === 0) {
          // Standalone internal plugins (no dependencies) are auto-enabled on first boot,
          // unless they explicitly opt out via enabledByDefault: false.
          if (plugin.enabledByDefault === false) return;
          enabled.add(plugin.id);
          changed = true;
          return;
        }
        const allRequirementsEnabled = requires.every((depId) => enabled.has(depId) || manifestDefaults.has(depId));
        if (allRequirementsEnabled) {
          enabled.add(plugin.id);
          changed = true;
        }
      });
    }

    return Array.from(enabled);
  }

  useEffect(() => {
    let active = true;

    if (manifest) {
      setResolvedManifest(manifest);
      return;
    }

    // Wait for MSAL to finish redirect/login processing before
    // attempting to load the manifest with a token. This prevents
    // a failed first attempt that produces "No accounts found" warnings.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 2000);

    async function loadManifest() {
      try {
        const result = await loadManifestSources('installed', {
          signal: controller.signal,
          getAccessToken,
        });
        if (!active) {
          return;
        }
        setResolvedManifest(result.merged);
      } catch {
        if (active) {
          setResolvedManifest([]);
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    void loadManifest();

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [getAccessToken, manifest, inProgress]);

  useEffect(() => {
    if (!hydrated || booted) {
      return;
    }

    const basePlugins = plugins;
    const hasKnowledgeCapture = basePlugins.some((plugin) => plugin.id === 'knowledge-capture');
    const manifestHasKnowledgeCapture = (resolvedManifest ?? []).some(
      (entry) => entry.id === 'knowledge-capture'
    );
    const resolvedPlugins = !hasKnowledgeCapture && !manifestHasKnowledgeCapture
      ? [...basePlugins, knowledgeCapturePlugin]
      : basePlugins;

    const mergedEnabledIds = mergeNewDefaultEnablement({
      enabledIds,
      seenPluginIds,
      plugins: resolvedPlugins,
      manifest: resolvedManifest,
    });

    void bootstrapPlugins({
      plugins: resolvedPlugins,
      manifest: resolvedManifest,
      hostVersion,
      hostCapabilities,
      enabledPluginIds: mergedEnabledIds,
    }).then((resolvedIds) => {
      setEnabledIds(resolvedIds);
      setErrors(pluginRegistry.getPluginErrors());
      const knownIds = Array.from(
        new Set([
          ...resolvedPlugins.map((plugin) => plugin.id),
          ...(resolvedManifest ?? []).map((entry) => entry.id),
        ])
      );
      setSeenPluginIds(Array.from(new Set([...seenPluginIds, ...knownIds])));
      setBooted(true);
    });
  }, [
    booted,
    enabledIds,
    seenPluginIds,
    hostCapabilities,
    hostVersion,
    hydrated,
    plugins,
    resolvedManifest,
    setBooted,
    setEnabledIds,
    setSeenPluginIds,
    setErrors,
  ]);

  // After boot, fetch org + workspace plugin overrides from server and apply them.
  // Runs whenever workspace changes so admins switching workspaces see the right state.
  useEffect(() => {
    if (!booted || !activeWorkspaceId || !isProtectedAppRoute) {
      return;
    }

    getAccessToken()
      .then(async (token) => {
        if (!token) {
          return;
        }
        await Promise.all([
          fetchOrgPluginSettings(token),
          fetchWorkspaceSettings(activeWorkspaceId, token),
          platformSettingsLoaded ? Promise.resolve() : fetchPlatformSettings(token),
        ]);
        await fetchVoiceSettings(activeWorkspaceId, token);
      })
      .catch(() => undefined);
  }, [
    booted,
    activeWorkspaceId,
    isProtectedAppRoute,
    getAccessToken,
    fetchOrgPluginSettings,
    fetchPlatformSettings,
    fetchWorkspaceSettings,
    fetchVoiceSettings,
    platformSettingsLoaded,
  ]);

  // Apply workspace settings to the registry whenever they change.
  // Also enforce org-level constraints: plugins disabled at org level cannot be enabled.
  useEffect(() => {
    if (!booted) {
      return;
    }
    // Wait for at least one of workspace or org settings to be available
    if (workspaceSettings.length === 0 && !orgSettingsLoaded) {
      return;
    }

    const currentEnabled = new Set(pluginRegistry.getEnabledPlugins().map((p) => p.id));
    const lockedPlugins = new Set(
      pluginRegistry.getAllPlugins()
        .filter((plugin) => plugin.locked)
        .map((plugin) => plugin.id)
    );
    const orgDisabledIds = getOrgDisabledIds();

    workspaceSettings.forEach(({ pluginId, enabled }) => {
      if (lockedPlugins.has(pluginId)) {
        currentEnabled.add(pluginId);
        return;
      }

      // Enforce org constraint: cannot enable a plugin disabled at org level
      if (orgDisabledIds.has(pluginId)) {
        currentEnabled.delete(pluginId);
        return;
      }

      const isEnabled = currentEnabled.has(pluginId);
      if (enabled && !isEnabled) {
        currentEnabled.add(pluginId);
      } else if (!enabled && isEnabled) {
        currentEnabled.delete(pluginId);
      }
    });

    // Also remove any currently enabled plugins that are org-disabled
    // (even if no workspace setting exists for them)
    orgDisabledIds.forEach((pluginId) => {
      if (!lockedPlugins.has(pluginId)) {
        currentEnabled.delete(pluginId);
      }
    });

    const next = Array.from(currentEnabled);
    pluginRegistry.setEnabledPluginIds(next);
    setEnabledIds(next);
  }, [booted, workspaceSettings, orgPluginSettings, orgSettingsLoaded, setEnabledIds, getOrgDisabledIds]);

  useEffect(() => {
    if (subscribedRef.current) {
      return;
    }

    subscribedRef.current = true;
    const unsubscribe = pluginRegistry.subscribe(() => {
      const enabled = pluginRegistry.getEnabledPlugins().map((plugin) => plugin.id);
      setEnabledIds(enabled);
      setErrors(pluginRegistry.getPluginErrors());
      void syncServerEnablement(enabled);
    });

    return () => {
      subscribedRef.current = false;
      unsubscribe();
    };
  }, [setEnabledIds, setErrors]);

  return null;
}
